/**
 * Announcement Card Component
 * 
 * Displays an announcement card for dashboard display
 */

import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Megaphone, Calendar, FileText, ExternalLink, Eye } from 'lucide-react';
import { Announcement } from '../../@types/announcement';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';
import { AnnouncementPriorityBadge } from './AnnouncementPriorityBadge';

interface AnnouncementCardProps {
  announcement: Announcement;
  onView?: (announcementId: string) => void;
  variant?: 'desktop' | 'mobile';
}

const Card = styled.div<{ priority: string; variant: string }>`
  background: white;
  border-radius: ${BorderRadius.lg};
  border-left: 4px solid ${props => {
    switch (props.priority) {
      case 'CRITICAL': return '#DC2626';
      case 'HIGH': return '#EA580C';
      case 'MEDIUM': return '#F59E0B';
      case 'LOW': return '#3B82F6';
      default: return ThemeColors.gray[400];
    }
  }};
  box-shadow: ${Shadows.sm};
  padding: ${Spacing.lg};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    box-shadow: ${Shadows.md};
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${Spacing.md};
  gap: ${Spacing.md};
`;

const CardTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardContent = styled.p`
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.secondary};
  margin: 0 0 ${Spacing.md} 0;
  line-height: ${Typography.lineHeight.relaxed};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${Spacing.sm};
  margin-top: ${Spacing.md};
  padding-top: ${Spacing.md};
  border-top: 1px solid ${ThemeColors.border.light};
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  background: ${ThemeColors.primary.light};
  color: ${ThemeColors.primary.dark};
  border: none;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${ThemeColors.primary.main};
    color: white;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const AttachmentBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  background: ${ThemeColors.background.secondary};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  margin-bottom: ${Spacing.sm};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const formatDate = (timestamp: any): string => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const AnnouncementCard: React.FC<AnnouncementCardProps> = ({
  announcement,
  onView,
  variant = 'desktop'
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onView) {
      onView(announcement.id);
    } else {
      navigate(`/announcements/${announcement.id}`);
    }
  };

  const handleDetailsLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (announcement.detailsLink) {
      window.open(announcement.detailsLink, '_blank');
    }
  };

  const isViewed = announcement.views.some(view => {
    // This would need to check against current user ID
    return false; // Placeholder
  });

  return (
    <Card priority={announcement.priority} variant={variant} onClick={handleClick}>
      <CardHeader>
        <CardTitle>{announcement.title}</CardTitle>
        <AnnouncementPriorityBadge priority={announcement.priority} size="sm" />
      </CardHeader>

      {announcement.attachmentName && (
        <AttachmentBadge>
          <FileText />
          {announcement.attachmentName}
        </AttachmentBadge>
      )}

      <CardContent>{announcement.content}</CardContent>

      <CardFooter>
        <CardMeta>
          <MetaItem>
            <Calendar />
            {formatDate(announcement.createdAt)}
          </MetaItem>
          {announcement.createdByName && (
            <MetaItem>
              <Megaphone />
              {announcement.createdByName}
            </MetaItem>
          )}
          {announcement.expiresAt && (
            <MetaItem>
              Expires: {formatDate(announcement.expiresAt)}
            </MetaItem>
          )}
          {!isViewed && (
            <MetaItem>
              <Eye />
              New
            </MetaItem>
          )}
        </CardMeta>

        <CardActions>
          {announcement.detailsLink && (
            <ActionButton onClick={handleDetailsLink}>
              <ExternalLink />
              Details
            </ActionButton>
          )}
          <ActionButton>
            View
          </ActionButton>
        </CardActions>
      </CardFooter>
    </Card>
  );
};

